import { AuthenticationService } from 'src/app/core/services/auth.service';
import { catchError, firstValueFrom, forkJoin, tap, throwError } from 'rxjs';
import { CompletedModalFormComponent } from 'src/app/forms-modal/_pop-ups/completed-modal-form/completed-modal-form.component';
import { Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ConfigSystemParams, OurColors, TypeLicence, TypeLicenceStatus } from 'src/app/core/consts';
import { ConfigSystemService } from 'src/app/core/services/utelize/system/config-system/config-system-service';
import { ConfirmationModalFormComponent } from 'src/app/forms-modal/_pop-ups/confirmation-modal-form/confirmation-modal-form.component';
import { DevicesService } from 'src/app/core/services/utelize/system/device/devices-service';
import { InputModalFormComponent } from 'src/app/forms-modal/_inputs/input-form/input-modal-form.component';
import { ExceptionsService } from 'src/app/core/services/utelize/general/exceptions-service';
import { LicenceCompanyAllocatedDTO } from 'src/app/core/models/system/licence/licence-company-allocated.dto';
import { LicencesCompanyAllocatedService } from 'src/app/core/services/utelize/system/licence/licences-company-allocated-service';
import { WarningModalFormComponent } from 'src/app/forms-modal/_pop-ups/warning-modal-form/warning-modal-form.component';

@Component({
  selector: 'app-licences-company-allocated-cards',
  templateUrl: './licences-company-allocated-cards.component.html',
  styleUrls: ['./licences-company-allocated-cards.component.scss']
})
export class LicencesCompanyAllocatedCardsComponent implements OnInit {

  @Input() companyId : number  | undefined;
  @Input() newCompany: boolean | undefined;

  @Output() licenceDataHasBeenSaved: EventEmitter<any>=new EventEmitter<any>();
  
  @ViewChild('cancelLicenceConfirmationModal', { static: false }) cancelLicenceConfirmationModal!: ConfirmationModalFormComponent;
  @ViewChild('completedModalForm',             { static: false }) completedModalForm!            : CompletedModalFormComponent;
  @ViewChild('deleteLicenceConfirmationModal', { static: false }) deleteLicenceConfirmationModal!: ConfirmationModalFormComponent;
  @ViewChild('inputModalForm',                 { static: false }) inputModalForm!                : InputModalFormComponent;
  @ViewChild('warningModalForm',               { static: false }) warningModalForm!              : WarningModalFormComponent;
  
  get allowOverrideStartDate          () { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.LicencesAllowOverrideStartDate,     false); }
  get hideEndedLicences               () { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.LicencesEndedHide,                  false); }
  get multiplePlatformQuantity        () { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.LicencesPlatformMultipleQuantity,   false); }
  get multiplePoolBundleQuantity      () { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.LicencesPoolBundleMultipleQuantity, false); }
  get onlyDeleteUnusedLicencesPlatform() { return this.configSystemService.getValueAsBoolean(ConfigSystemParams.LicencesPlatformOnlyDeleteUnused,   true ); }
  
  private invalidAction                  : string="Invalid Action";
  private selectedLicenceCompanyAllocated: LicenceCompanyAllocatedDTO | undefined;

  public allocatedLicences    : LicenceCompanyAllocatedDTO[]=[];
  public enumTextBundle       : string=TypeLicence.Bundle;
  public enumTextFeature      : string=TypeLicence.Feature;
  public enumTextPlatform     : string=TypeLicence.Platform;
  public enumTextPoolBundle   : string=TypeLicence.PoolBundle;
  public enumTextUsage        : string=TypeLicence.Usage;
  public totalPlatformLicences: number=0;

  constructor(
    private authenticationService          : AuthenticationService,
    private configSystemService            : ConfigSystemService,
    private devicesService                 : DevicesService,
    private exceptionsService              : ExceptionsService,
    private licencesCompanyAllocatedService: LicencesCompanyAllocatedService
  ) { }

  public ngOnInit(): void {
    try {
      if (this.newCompany) {
        this.companyId=0;
      
      } else if (!this.companyId || Number(this.companyId)<0) {
        throw new Error("LicencesCompanyAllocatedCardsComponent..CompanyID cannot be determined");
      }

      this.refresh();
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public refresh() {
    try {
      this.licencesCompanyAllocatedService.getLicencesCompanyAllocatedForSingleCompany(Number(this.companyId))
      .pipe(
        catchError((error) => {
          return throwError(() => new Error(this.exceptionsService.formatHttpError(error)))
        })
      )
      .subscribe({
        next: data => {
          this.allocatedLicences=this.hideEndedLicences
                                  ? data.filter(l => this.getLicenceStatus(l) != TypeLicenceStatus.EndedLicence)
                                  : data;
        },
        error: error => {
          this.exceptionsService.handleException(new Error(error));
        }
      });

      this.licencesCompanyAllocatedService.totalLivePlatformLicencesForCompanyExcludeEnded(Number(this.companyId))
      .pipe(
        catchError((error) => {
          return throwError(() => new Error(this.exceptionsService.formatHttpError(error)))
        })
      )
      .subscribe({
        next: data => {
          this.totalPlatformLicences=data?.result ?? 0;
        },
        error: error => {
          this.exceptionsService.handleException(new Error(error));
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public getLicenceStatus(licenceCompanyAllocated: LicenceCompanyAllocatedDTO): string {
    try {
      return licenceCompanyAllocated.endDate &&
             !licenceCompanyAllocated.isBilling ? TypeLicenceStatus.EndedLicence
                                                : (licenceCompanyAllocated.endDate ? TypeLicenceStatus.AboutToEnd
                                                                                   : (licenceCompanyAllocated.isBilling ? TypeLicenceStatus.CurrentlyBilling
                                                                                                                        : TypeLicenceStatus.YetToBill
                                                                                     )
                                                  );
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return TypeLicenceStatus.Unknown;
    } 
  }

  //#region Quantity

  public showEditQuantityIcon(licenceCompanyAllocated: LicenceCompanyAllocatedDTO) {
    try {
      return (licenceCompanyAllocated) &&
             (
              this.canEditQuantity(licenceCompanyAllocated) ||
              this.allowOverrideStartDate
             )
             && this.authenticationService.canEditSystemOwned(licenceCompanyAllocated.systemOwned);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  private canEditQuantity(licenceCompanyAllocated: LicenceCompanyAllocatedDTO): boolean {
    try {
      return licenceCompanyAllocated.licenceType==TypeLicence.Bundle ||
             (this.multiplePoolBundleQuantity && licenceCompanyAllocated.licenceType==TypeLicence.PoolBundle) ||
             (licenceCompanyAllocated.licenceType==TypeLicence.Platform && !!licenceCompanyAllocated.platformAddOn);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    }
  }

  public changeQuantity(licenceCompanyAllocated: LicenceCompanyAllocatedDTO) {
    try {
      const licenceStatus   : string =this.getLicenceStatus(licenceCompanyAllocated);
      const quantityEditable: boolean=this.canEditQuantity(licenceCompanyAllocated);
      const dateEditable    : boolean=this.allowOverrideStartDate;

      if (!quantityEditable && !dateEditable)
        this.warningModalForm.openForm(this.invalidAction, `Licences of type '${licenceCompanyAllocated.licenceType}' cannot have quantity changed`);

      else if (!this.authenticationService.canEditSystemOwned(licenceCompanyAllocated.systemOwned))
        this.warningModalForm.openForm(this.invalidAction, `System Owned Licences cannot have quantity changed`);

      else if (licenceStatus != TypeLicenceStatus.YetToBill &&
               !(licenceCompanyAllocated.licenceType==TypeLicence.Platform && licenceCompanyAllocated.platformAddOn))
        this.warningModalForm.openForm(this.invalidAction,
                                       `Only licences with a status of '${TypeLicenceStatus.YetToBill}' can have quantity` +
                                       (this.allowOverrideStartDate ? " and/or start date" : "") +
                                       ` changed`);

      else if (licenceCompanyAllocated.licenceType==TypeLicence.Platform &&
               licenceCompanyAllocated.platformAddOn &&
               (licenceStatus==TypeLicenceStatus.AboutToEnd || licenceStatus==TypeLicenceStatus.EndedLicence)
              )
        this.warningModalForm.openForm(this.invalidAction, `Platform Add-Ons with a status of '${licenceStatus}' cannot have quantity changed`);

      else {
        this.selectedLicenceCompanyAllocated=licenceCompanyAllocated;

        this.inputModalForm.formTitle   ="Edit Values";
        this.inputModalForm.fieldCaption="Quantity";
        this.inputModalForm.input0Show  =quantityEditable;
        this.inputModalForm.input0Value =quantityEditable ? <string><any>licenceCompanyAllocated.quantity : undefined;
        this.inputModalForm.date0Show   =dateEditable;
        this.inputModalForm.date0Caption="Start Date";
        this.inputModalForm.date0Value  =dateEditable ? this.startDateAsIsoForInput(licenceCompanyAllocated.startDate) : undefined;
        this.inputModalForm.openForm();
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  private formatNaiveDateAsDDMMYYYY(value: string | Date | undefined): string {
    try {
      if (!value)
        return "";

      const str : string=typeof value==="string" ? value : this.localDatePartsAsIso(<Date>value);
      const yyyy: string=str.substring(0, 4);
      const mm  : string=str.substring(5, 7);
      const dd  : string=str.substring(8, 10);

      return dd+"/"+mm+"/"+yyyy;
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return "";
    }
  }

  private localDatePartsAsIso(value: Date): string {
    try {
      return value.getFullYear()+"-"+
             String(value.getMonth()+1).padStart(2, "0")+"-"+
             String(value.getDate ()  ).padStart(2, "0");
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return "";
    }
  }

  private startDateAsIsoForInput(value: Date | string | undefined): string | undefined {
    try {
      if (!value)
        return undefined;

      const str: string=typeof value==="string"
                        ? value
                        : this.localDatePartsAsIso(<Date>value);

      return str.substring(0, 10);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return undefined;
    }
  }
  
  public inputModalFormSubmitted(inputValue: string) {
    try {
      const _inputValue=Number(inputValue);

      if (isNaN(_inputValue)) {
        throw new Error("The quantity submitted is not a valid number.");
      }

      this.updateQuantity(Number(_inputValue), undefined);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public inputModalFormSubmittedExt(values: { input0: string | undefined, date0: string | undefined }) {
    try {
      const existingQuantity: number=Number(this.selectedLicenceCompanyAllocated?.quantity ?? 1);
      let   quantity        : number=existingQuantity;

      if (values.input0 != undefined) {
        const parsed: number=Number(values.input0);

        if (isNaN(parsed)) {
          throw new Error("LicencesCompanyAllocatedCardsComponent.inputModalFormSubmittedExt() - The quantity submitted is not a valid number.");
        }

        quantity=parsed;
      }

      const startDate: Date | undefined=values.date0 ? new Date(values.date0) : undefined;

      this.updateQuantity(quantity, startDate);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  public async updateQuantity(quantity: number, startDate: Date | undefined) {
    try {
      let proceed = true;
      if (Number(this.selectedLicenceCompanyAllocated?.quantity) > Number(quantity) && this.selectedLicenceCompanyAllocated != null) {
        let licenceToCheck: LicenceCompanyAllocatedDTO = structuredClone(this.selectedLicenceCompanyAllocated);
        licenceToCheck.quantity = Number(this.selectedLicenceCompanyAllocated?.quantity) - Number(quantity);
        proceed = await this.licenceReductionCheck(licenceToCheck);
      }

      if (proceed == true) {
        this.licencesCompanyAllocatedService.changeQuantityAndStartDate(<number>this.selectedLicenceCompanyAllocated?.id,
                                                                        <number>this.selectedLicenceCompanyAllocated?.companyID,
                                                                        quantity,
                                                                        startDate)
        .pipe(
          catchError((error) => {
            return throwError(() => new Error(this.exceptionsService.formatHttpError(error)))
          })
        )
        .subscribe({
          next: data => {
            this.refresh();
          },
          error: error => {
            this.exceptionsService.handleException(new Error(error));
          }
        });
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }

  //#endregion

  //#region Delete/Cancel Licence

  public canLicenceBeEnded(licenceCompanyAllocated: LicenceCompanyAllocatedDTO) {
    try {
      const licenceStatus: string =this.getLicenceStatus(licenceCompanyAllocated);

      if (!this.authenticationService.canEditSystemOwned(licenceCompanyAllocated.systemOwned))
        this.warningModalForm.openForm(this.invalidAction, `System Owned Licences cannot be removed or cancelled`);

      else if (licenceStatus==TypeLicenceStatus.AboutToEnd)
        this.warningModalForm.openForm(this.invalidAction, `The Licence has already been cancelled`);

      else if (licenceStatus==TypeLicenceStatus.EndedLicence)
        this.warningModalForm.openForm(this.invalidAction, `The Licence has already ended`);

      return !((!this.authenticationService.canEditSystemOwned(licenceCompanyAllocated.systemOwned))
               || (licenceStatus==TypeLicenceStatus.AboutToEnd)
               || (licenceStatus==TypeLicenceStatus.EndedLicence));
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    } 
  }

  public async licenceReductionCheck(licenceCompanyAllocated: LicenceCompanyAllocatedDTO) {
    try {
      if (this.canLicenceBeEnded(licenceCompanyAllocated)) {
        const { totalLiveDevicesResult,
                totalPlatformLicencesResult }= await firstValueFrom(
                  forkJoin({
                    totalLiveDevicesResult      : this.devicesService.totalLiveDevicesForCompany(licenceCompanyAllocated.companyID),
                    totalPlatformLicencesResult : this.licencesCompanyAllocatedService.totalLivePlatformLicencesForCompanyExcludeEnded(licenceCompanyAllocated.companyID)
                  })
                );
  
        const totalLiveDevices         : number=totalLiveDevicesResult.result;
        const totalPlatformLicences    : number=totalPlatformLicencesResult.result;
        const numberOfLicencesToRemove : number=(licenceCompanyAllocated.numberOfEntities ?? 0)*(licenceCompanyAllocated.quantity ?? 1);
        const numberOfRemainingLicences: number=totalPlatformLicences-numberOfLicencesToRemove;
        const companyName              : string=licenceCompanyAllocated.companyName ?? "This company";
        
        if (this.getLicenceStatus(licenceCompanyAllocated) != TypeLicenceStatus.YetToBill
            && totalLiveDevices>(numberOfRemainingLicences)) {
          const s: string=`${companyName} has a total of ${totalPlatformLicences} Licences and ${totalLiveDevices} ${totalLiveDevices==1 ? "has" : "have"} been used<br><br>` +
                          `Removing ${numberOfLicencesToRemove} ${numberOfLicencesToRemove==1 ? "Licence" : "Licences"} will give a remaining total of ${numberOfRemainingLicences} ${numberOfRemainingLicences==1 ? "Licence" : "Licences"}`;
          
          this.warningModalForm.openForm("Cannot proceed", s);
          return false;
        } else {
          return true;
        }
      } else {
        return false;
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    } 
  }

  public showDeleteIcon(licenceCompanyAllocated: LicenceCompanyAllocatedDTO) {
    try {
      return (licenceCompanyAllocated) &&
              this.authenticationService.canEditSystemOwned(licenceCompanyAllocated.systemOwned);
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
      return false;
    } 
  }

  public async startCancelLicence(licenceCompanyAllocated: LicenceCompanyAllocatedDTO) {
    try {
      if (!this.onlyDeleteUnusedLicencesPlatform || licenceCompanyAllocated.licenceType != TypeLicence.Platform) {
        this.furtherChecksBeforeCancelLicence(licenceCompanyAllocated);

      } else if (licenceCompanyAllocated.companyID && Number(licenceCompanyAllocated.companyID)>0) {
        if (await this.licenceReductionCheck(licenceCompanyAllocated)) {
          this.furtherChecksBeforeCancelLicence(licenceCompanyAllocated);
        }
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    } 
  }

  public furtherChecksBeforeCancelLicence(licenceCompanyAllocated: LicenceCompanyAllocatedDTO) {
    try {
      const licenceStatus: string         =this.getLicenceStatus(licenceCompanyAllocated);
      this.selectedLicenceCompanyAllocated=licenceCompanyAllocated;

      if (!this.authenticationService.canEditSystemOwned(licenceCompanyAllocated.systemOwned))
        this.warningModalForm.openForm(this.invalidAction, `System Owned Licences cannot be removed or cancelled`);

      else if (licenceStatus==TypeLicenceStatus.AboutToEnd)
        this.warningModalForm.openForm(this.invalidAction, `The Licence has already been cancelled`);

      else if (licenceStatus==TypeLicenceStatus.EndedLicence)
        this.warningModalForm.openForm(this.invalidAction, `The Licence has already ended`);

      else if (licenceStatus==TypeLicenceStatus.YetToBill)
        this.deleteLicenceConfirmationModal.openForm("WARNING",
                                                     `Are you sure you want to delete this '${licenceStatus}' licence?`,
                                                     undefined,
                                                     OurColors.Red,
                                                     OurColors.DarkBlue);

      else {
        this.areYouSureCancelLicence();
      }
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    } 
  }
  
  public areYouSureCancelLicence() {
    try {
      this.licencesCompanyAllocatedService.calculateCancellationDate(this.selectedLicenceCompanyAllocated?.id ?? -1, this.selectedLicenceCompanyAllocated?.companyID ?? -1)
      .pipe(
        catchError((error) => {
          return throwError(() => new Error(this.exceptionsService.formatHttpError(error)))
        })
      )
      .subscribe({
        next: data => {
          if (!data || !data.result) {
            this.exceptionsService.handleException(new Error("LicencesCompanyAllocatedCardsComponent - Licence End Date cannot be calculated"));
          
          } else {
            this.cancelLicenceConfirmationModal.openForm("WARNING",
                                                         `Are you sure you want to cancel this licence with an End Date of ${this.formatNaiveDateAsDDMMYYYY(data.result)}?`,
                                                         undefined,
                                                         OurColors.Red,
                                                         OurColors.DarkBlue);
          }
        },
        error: error => {
          this.exceptionsService.handleException(new Error(error));
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public yesCancelLicence() {
    try {
      this.licencesCompanyAllocatedService.cancelLicence(this.selectedLicenceCompanyAllocated?.id ?? -1, this.selectedLicenceCompanyAllocated?.companyID ?? -1)
      .pipe(
        catchError((error) => {
          return throwError(() => new Error(this.exceptionsService.formatHttpError(error)))
        })
      )
      .subscribe({
        next: data => {
          this.refresh();
          this.completedModalForm.openForm("Complete",
                                           "The licence has been cancelled",
                                           "https://cdn.lordicon.com/sjoccsdj.json",
                                           OurColors.Red,
                                           OurColors.DarkBlue);
        },
        error: error => {
          this.exceptionsService.handleException(new Error(error));
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
  
  public yesDeleteLicence() {
    try {
      this.licencesCompanyAllocatedService.delete(this.selectedLicenceCompanyAllocated?.id ?? -1)
      .pipe(
        catchError((error) => {
          return throwError(() => new Error(this.exceptionsService.formatHttpError(error)))
        })
      )
      .subscribe({
        next: data => {
          this.refresh();
          this.completedModalForm.openForm("Complete",
                                           "The licence has been deleted",
                                           "https://cdn.lordicon.com/sjoccsdj.json",
                                           OurColors.Red,
                                           OurColors.DarkBlue);
        },
        error: error => {
          this.exceptionsService.handleException(new Error(error));
        }
      });
    }
    catch(e) {
      this.exceptionsService.handleException(<Error>e);
    }
  }
}
